/**
 * Logfire Configuration Section Component
 *
 * Inline section for managing Logfire tokens - used in full-page setup flow.
 * This is a non-modal version of LogfireConfigDialog for embedding in pages.
 */

import { useState } from "react";
import { Plus, CheckCircle2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { LogfireTokensList } from "./LogfireTokensList";
import { useCreateLogfireToken } from "@/hooks/useLogfireTokens";
import { LogfireLogo } from "./logos";
import type { LogfireTokenCreate } from "@/types/integrations";

interface LogfireConfigSectionProps {
  /** Whether this section is expanded */
  expanded?: boolean;
  /** Callback when expand state changes */
  onExpandChange?: (expanded: boolean) => void;
  /** Called when a token is successfully created */
  onTokenCreated?: () => void;
}

export function LogfireConfigSection({
  expanded = false,
  onExpandChange,
  onTokenCreated,
}: LogfireConfigSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<LogfireTokenCreate>({
    read_token: "",
    project_name: "",
    project_url: null,
    region: "us",
  });

  const createTokenMutation = useCreateLogfireToken();

  const handleReset = () => {
    setFormData({
      read_token: "",
      project_name: "",
      project_url: null,
      region: "us",
    });
    setShowAddForm(false);
    createTokenMutation.reset();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.project_name.trim() || !formData.read_token.trim()) {
      return;
    }

    try {
      await createTokenMutation.mutateAsync({
        ...formData,
        project_name: formData.project_name.trim(),
        read_token: formData.read_token.trim(),
        project_url: formData.project_url?.trim() || null,
      });
      handleReset();
      onTokenCreated?.();
    } catch {
      // Error will be shown via createTokenMutation.error
    }
  };

  const isFormValid = formData.project_name.trim() && formData.read_token.trim();

  return (
    <Card className={expanded ? "ring-2 ring-primary" : ""}>
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => onExpandChange?.(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-muted/80 to-muted/40 flex items-center justify-center shadow-sm">
              <LogfireLogo className="w-8 h-8" />
            </div>
            <div>
              <CardTitle className="text-lg">Logfire</CardTitle>
              <CardDescription className="text-sm">
                Monitor and analyze LLM application logs in real-time
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="icon">
            {expanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-6 pt-0">
          <Separator />

          {/* Existing Tokens List */}
          <div>
            <h3 className="text-sm font-medium mb-3">Configured Tokens</h3>
            <LogfireTokensList />
          </div>

          <Separator />

          {/* Add Token Section */}
          <div>
            {!showAddForm ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Token
              </Button>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="text-sm font-medium">Add New Token</h3>

                {/* Success Alert */}
                {createTokenMutation.isSuccess && (
                  <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Token validated successfully!</p>
                      <p className="text-xs mt-1">
                        Found {createTokenMutation.data?.validation.records_found || 0} records
                      </p>
                    </div>
                  </div>
                )}

                {/* Error Alert */}
                {createTokenMutation.isError && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Validation failed</p>
                      <p className="text-xs mt-1">
                        {createTokenMutation.error?.message || "Unable to validate token"}
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="project-name">
                    Project Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="project-name"
                    placeholder="my-llm-project"
                    value={formData.project_name}
                    onChange={(e) =>
                      setFormData({ ...formData, project_name: e.target.value })
                    }
                    autoComplete="off"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="read-token">
                    Read Token <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="read-token"
                    type="password"
                    placeholder="Your Logfire read token"
                    value={formData.read_token}
                    onChange={(e) =>
                      setFormData({ ...formData, read_token: e.target.value })
                    }
                    autoComplete="off"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Find your read token in Logfire project settings
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project-url">Project URL (optional)</Label>
                  <Input
                    id="project-url"
                    type="url"
                    placeholder="https://logfire.pydantic.dev/your-org/your-project"
                    value={formData.project_url || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        project_url: e.target.value || null,
                      })
                    }
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">Region</Label>
                  <Select
                    value={formData.region}
                    onValueChange={(value) =>
                      setFormData({ ...formData, region: value as "us" | "eu" })
                    }
                  >
                    <SelectTrigger id="region">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">US</SelectItem>
                      <SelectItem value="eu">EU</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    disabled={createTokenMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isFormValid || createTokenMutation.isPending}
                  >
                    {createTokenMutation.isPending ? "Validating..." : "Add Token"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
